import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { purchaseRequestsApi, vendorsApi, departmentsApi } from '../lib/api';
import { PR_TYPE_LABELS, SPEND_CATEGORIES } from '../types';
import type { Vendor, Department, CreatePurchaseRequestDto, PrType } from '../types';
import AppShell from '../components/AppShell';

const SANS = "'Roboto', system-ui, sans-serif";

export default function NewPurchaseRequestPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<{
    title: string;
    type: PrType;
    amount: string;
    vendorId: string;
    vendorName: string;
    departmentId: string;
    description: string;
    businessCase: string;
    spendCategory: string;
    seats: string;
    periodStart: string;
    periodEnd: string;
  }>({
    title: '', type: 'new_purchase', amount: '', vendorId: '', vendorName: '',
    departmentId: '', description: '', businessCase: '', spendCategory: '',
    seats: '', periodStart: '', periodEnd: '',
  });

  useEffect(() => {
    if (!user) return;
    Promise.all([vendorsApi.list(), departmentsApi.list()]).then(([v, d]) => {
      setVendors(v);
      setDepartments(d);
      if (user.departmentId) {
        setForm((f) => ({ ...f, departmentId: user.departmentId! }));
      }
    });
  }, [user]);

  if (loading || !user) return null;

  const field = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async (andSubmit: boolean) => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0) { setError('Valid amount is required'); return; }

    setError(null);
    setSubmitting(true);

    try {
      const dto: CreatePurchaseRequestDto = {
        title: form.title.trim(),
        type: form.type,
        amount,
        ...(form.vendorId ? { vendorId: form.vendorId } : {}),
        ...(form.vendorName.trim() && !form.vendorId ? { vendorName: form.vendorName.trim() } : {}),
        ...(form.departmentId ? { departmentId: form.departmentId } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        ...(form.businessCase.trim() ? { businessCase: form.businessCase.trim() } : {}),
        ...(form.spendCategory ? { spendCategory: form.spendCategory } : {}),
        ...(form.seats ? { seats: parseInt(form.seats, 10) } : {}),
        ...(form.periodStart ? { periodStart: form.periodStart } : {}),
        ...(form.periodEnd ? { periodEnd: form.periodEnd } : {}),
      };

      const pr = await purchaseRequestsApi.create(dto);

      if (andSubmit) {
        await purchaseRequestsApi.submit(pr.id);
      }

      navigate(`/purchase-requests/${pr.id}`);
    } catch (e: any) {
      setError(e.message ?? 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell user={user} title="New Purchase Request">
      <div style={{ maxWidth: '720px' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <Link to="/purchase-requests" style={{ fontSize: '0.8rem', color: '#94a3b8', textDecoration: 'none' }}>
            ← Back to requests
          </Link>
        </div>

        {error && <ErrorBanner message={error} />}

        <div style={card}>
          <Field label="Title *">
            <input
              value={form.title}
              onChange={field('title')}
              placeholder="e.g. GitHub Teams — 50 seats renewal"
              style={inputStyle}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Field label="Type *">
              <select value={form.type} onChange={field('type')} style={inputStyle}>
                {(Object.keys(PR_TYPE_LABELS) as PrType[]).map((t) => (
                  <option key={t} value={t}>{PR_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </Field>
            <Field label="Spend category">
              <select value={form.spendCategory} onChange={field('spendCategory')} style={inputStyle}>
                <option value="">Select category</option>
                {SPEND_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Field label="Amount (USD) *">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={field('amount')}
                placeholder="0.00"
                style={inputStyle}
              />
            </Field>
            <Field label="Seats / licences">
              <input
                type="number"
                min="1"
                value={form.seats}
                onChange={field('seats')}
                placeholder="e.g. 50"
                style={inputStyle}
              />
            </Field>
          </div>

          <Field label="Vendor">
            <select value={form.vendorId} onChange={field('vendorId')} style={inputStyle}>
              <option value="">Select existing vendor…</option>
              {vendors.filter((v) => v.status === 'active').map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </Field>

          {!form.vendorId && (
            <Field label="New vendor name">
              <input
                value={form.vendorName}
                onChange={field('vendorName')}
                placeholder="Enter vendor name if not in list"
                style={inputStyle}
              />
            </Field>
          )}

          <Field label="Department">
            <select value={form.departmentId} onChange={field('departmentId')} style={inputStyle}>
              <option value="">Select department…</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Field label="Period start">
              <input type="date" value={form.periodStart} onChange={field('periodStart')} onKeyDown={(e) => e.preventDefault()} style={dateInputStyle} />
            </Field>
            <Field label="Period end">
              <input type="date" value={form.periodEnd} onChange={field('periodEnd')} onKeyDown={(e) => e.preventDefault()} style={dateInputStyle} />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={form.description}
              onChange={field('description')}
              rows={3}
              placeholder="What does this purchase cover?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <Field label="Business case">
            <textarea
              value={form.businessCase}
              onChange={field('businessCase')}
              rows={3}
              placeholder="Why is this purchase needed? What's the business impact?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </Field>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              onClick={() => handleSave(false)}
              disabled={submitting}
              style={ghostBtn}
            >
              Save as Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={submitting}
              style={primaryBtn}
            >
              {submitting ? 'Submitting…' : 'Submit for Approval'}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      <label style={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '0.75rem 1rem', color: '#991b1b',
      fontSize: '0.85rem', marginBottom: '1.25rem',
    }}>
      {message}
    </div>
  );
}

const card: React.CSSProperties = {
  backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 10, padding: '2rem',
  display: 'flex', flexDirection: 'column', gap: '1.25rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: '0.6rem 0.85rem', color: '#0f172a', fontSize: '0.9rem',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', fontWeight: 300,
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

// colorScheme: 'light' ensures the native date picker renders correctly on white bg
const dateInputStyle: React.CSSProperties = {
  ...inputStyle,
  colorScheme: 'light',
};

const primaryBtn: React.CSSProperties = {
  backgroundColor: '#0ea5e9', color: '#ffffff', border: 'none', borderRadius: 8,
  padding: '0.65rem 1.5rem', fontSize: '0.85rem', fontWeight: 400, cursor: 'pointer',
  fontFamily: SANS, boxShadow: '0 1px 3px rgba(14,165,233,0.3)',
};

const ghostBtn: React.CSSProperties = {
  backgroundColor: 'transparent', color: '#475569',
  border: '1px solid #e2e8f0', borderRadius: 8,
  padding: '0.65rem 1.5rem', fontSize: '0.85rem', cursor: 'pointer',
  fontFamily: SANS,
};
